import Vue from 'vue'
import Vuex from 'vuex'

import ErrorFactory from '../models/ErrorFactory'

Vue.use(Vuex)

const BASE_URL = process.env.BASE_URL
const BASE_URL_FORM = process.env.BASE_URL_FORM
const STORAGE_TOKEN_ID = 'COOKIE_TOKEN_ID'
const errorFactory = new ErrorFactory()

class LocalError extends Error {
  constructor (...args) {
    super(...args)
    this.response = {}
    this.response.data = new Error(...args)
  }
}

export function createStore (axios) {
  const store = new Vuex.Store({
    strict: true,

    state: {
      token: null,
      user: null,
      initialized: false,
      forms: [],
      currentForm: null,
      currentSubmission: null,
      axios: axios,
      base_url: BASE_URL,
      base_url_form: BASE_URL_FORM
    },

    getters: {
      initialized: state => state.initialized,
      me: state => state.user,
      forms: state => state.forms,
      currentForm: state => state.currentForm,
      currentSubmission: state => state.currentSubmission,
      axios: state => state.axios,
      base_url: state => state.base_url,
      base_url_form: state => state.base_url_form
    },

    actions: {
      async init ({ commit, dispatch }) {
        // Implement this root action in modules

        // Initial loading procedure
        // 1. Get the token from the local storage
        const token = localStorage.getItem(STORAGE_TOKEN_ID)
        commit('setToken', token)
        try {
          // 2. try to fetch the logged in user
          // (might fail) due to several reasons (e.g. token expired)
          await dispatch('currentUser')

          // 3. unlock the application and allow the router to redirect
          commit('setInitialized')
        } catch (error) {
          // 3. unlock the application and allow the router to redirect
          commit('setInitialized')
        }
      },
      async login ({ commit }, payload) {
        try {
          const { data } = await axios.post(BASE_URL + '/users/signin', {
            email: payload.email,
            password: payload.password
          })

          if (!data || !data.token) {
            throw new LocalError()
          }
          commit('setToken', data.token)
        } catch (error) {
          throw errorFactory.make(error.response.data, 'Login failed!')
        }
      },
      async signup ({ commit }, payload) {
        try {
          const { data } = await axios.post(BASE_URL + '/users/signup', {
            email: payload.email,
            username: payload.username,
            firstname: payload.firstname,
            lastname: payload.lastname,
            password: payload.password
          })

          if (!data) {
            throw new LocalError()
          }
        } catch (error) {
          throw errorFactory.make(error.response.data, 'Signup failed!')
        }
      },
      async logout ({ commit }) {
        commit('setToken', null)
        commit('setUser', null)
        window.location.reload(true)
      },
      async currentUser ({ commit }) {
        try {
          const { data } = await axios.get(BASE_URL + '/users/current')

          if (!data || !data.email) {
            throw new LocalError()
          }

          commit('setUser', data)
        } catch (error) {
          commit('setToken', null)
          commit('setUser', null)
          throw errorFactory.make(error.response.data, 'Couldn\'t fetch current user!')
        }
      },
      async updateSettingsUserProfile ({ commit }, payload) {
        try {
          const { data } = await axios.put(BASE_URL + '/users/' + payload.id, {
            firstname: payload.firstname,
            lastname: payload.lastname
          })

          if (!data || !data.email) {
            throw new LocalError()
          }

          commit('setUser', data)
        } catch (error) {
          throw errorFactory.make(error.response.data, 'Profile update failed!')
        }
      },
      async updateAvatar ({ commit }, payload) {
        try {
          const formData = new FormData()
          formData.append('avatar', payload.file, payload.file.name)

          const { data } = await axios.post(BASE_URL + '/users/avatars', formData)

          if (!data || !data.email) {
            throw new LocalError()
          }

          commit('setUser', data)
        } catch (error) {
          throw errorFactory.make(error.response.data, 'Avatar upload failed!')
        }
      },
      async fetchForms ({ commit }) {
        try {
          const { data } = await axios.get(BASE_URL + '/forms')

          if (!data) {
            throw new LocalError()
          }

          commit('setForms', data)
        } catch (error) {
          throw errorFactory.make(error.response.data, 'Couldn\'t fetch forms!')
        }
      },
      async fetchCurrentForm ({ commit }, id) {
        commit('setCurrentForm', null)
        try {
          const { data } = await axios.get(BASE_URL + '/forms/' + id)

          if (!data) {
            throw new LocalError()
          }

          commit('setCurrentForm', data)
        } catch (error) {
          throw errorFactory.make(error.response.data, 'Couldn\'t fetch form!')
        }
      },
      async fetchCurrentSubmission ({ commit }, id) {
        commit('setCurrentSubmission', null)
        if (id === null || id === undefined) {
          return
        }

        try {
          const { data } = await axios.get(BASE_URL + '/submission/' + id)

          if (!data) {
            throw new LocalError()
          }

          commit('setCurrentSubmission', data)
        } catch (error) {
          throw errorFactory.make(error.response.data, 'Couldn\'t fetch submission!')
        }
      }
    },

    mutations: {
      setToken (state, token) {
        if (!token) {
          axios.defaults.headers.common['Authorization'] = undefined
          localStorage.removeItem(STORAGE_TOKEN_ID)
        } else {
          axios.defaults.headers.common['Authorization'] = 'Bearer ' + token
          localStorage.setItem(STORAGE_TOKEN_ID, token)
        }
        state.token = token
      },
      setUser (state, user) {
        if (user && user.avatar) {
          user.avatar = `${BASE_URL}${user.avatar.url}`
        } else if (user) {
          user.avatar = null
        }
        state.user = user
      },
      setForms (state, forms) {
        state.forms = forms
      },
      setCurrentForm (state, currentForm) {
        state.currentForm = currentForm
      },
      setCurrentSubmission (state, currentSubmission) {
        state.currentSubmission = currentSubmission
      },
      setInitialized (state) {
        state.initialized = true
      }
    },

    modules: { }
  })

  // Webpack HRM
  if (module.hot) {
    module.hot.accept([
      // './user'
    ], () => {
      store.hotUpdate({
        modules: {
          // user: require('./user').default
        }
      })
    })
  }

  return store
}



// WEBPACK FOOTER //
// ./src/store/index.js